import React, {useState} from "react";
import "./IaAgentsPage.css";
import {apiStartIaQuizSession, apiAnswerIaQuizQuestion, apiSubmitIaQuizResults, apiReportIaQuizQuestion} from "../api/iaQuiz";
import {useToast} from "../components/FeedbackProvider";
import {trackTestStarted, trackTestFinished} from "../analytics";

const REPORT_REASONS = ["ambiguë", "obsolète", "inexacte", "trop difficile", "autre"];

function feedbackTitle(feedback) {
    if (feedback.unknown) return "Bonne décision : mieux vaut le dire que deviner.";
    return feedback.correct ? "Bien vu !" : "À retenir";
}

function unknownMessage(count) {
    if (count === 0) return "Aucun « Je ne sais pas » : vous avez joué toutes les cartes du jeu.";
    if (count <= 2) return `${count} « Je ne sais pas » : une prudence bien placée, pas un recul.`;
    if (count <= 5) return `${count} « Je ne sais pas » : quelques zones à explorer, votre boussole fonctionne très bien.`;
    if (count <= 9) return `${count} « Je ne sais pas » : le terrain est cartographié ; il reste à partir en expédition.`;
    return `${count} « Je ne sais pas » : excellente honnêteté intellectuelle — le laboratoire vous attend.`;
}

export default function IaAgentsPage({onBack}) {
    const {notify} = useToast();
    const [phase, setPhase] = useState("home");
    const [token, setToken] = useState(null);
    const [question, setQuestion] = useState(null);
    const [index, setIndex] = useState(1);
    const [total, setTotal] = useState(15);
    const [selected, setSelected] = useState(null);
    const [showHint, setShowHint] = useState(false);
    const [answered, setAnswered] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [pendingNext, setPendingNext] = useState(null);
    const [result, setResult] = useState(null);
    const [reportState, setReportState] = useState("closed");
    const [reportReason, setReportReason] = useState(REPORT_REASONS[0]);
    const [loading, setLoading] = useState(false);

    async function start() {
        setLoading(true);
        const data = await apiStartIaQuizSession();
        setLoading(false);
        setToken(data.token);
        setQuestion(data.question);
        setIndex(data.index);
        setTotal(data.total);
        setSelected(null);
        setAnswered(false);
        setFeedback(null);
        setPendingNext(null);
        setReportState("closed");
        trackTestStarted("ia-agents");
        setPhase("quiz");
    }

    async function validate() {
        if (selected === null) return;
        setLoading(true);
        const {ok, data} = await apiAnswerIaQuizQuestion(token, selected);
        setLoading(false);
        if (!ok) {
            notify(data.error || "Une erreur est survenue, merci de réessayer.", "error");
            return;
        }
        setFeedback(data.feedback);
        setAnswered(true);
        setPendingNext(data);
    }

    async function proceed() {
        if (!pendingNext) return;
        if (pendingNext.done) {
            setLoading(true);
            const {ok, data} = await apiSubmitIaQuizResults(pendingNext.token);
            setLoading(false);
            if (!ok) {
                notify(data.error || "Une erreur est survenue, merci de réessayer.", "error");
                return;
            }
            setResult(data);
            trackTestFinished("ia-agents");
            setPhase("result");
            return;
        }
        setToken(pendingNext.token);
        setQuestion(pendingNext.question);
        setIndex(pendingNext.index);
        setSelected(null);
        setAnswered(false);
        setFeedback(null);
        setPendingNext(null);
        setShowHint(false);
        setReportState("closed");
    }

    async function sendReport() {
        if (!question) return;
        await apiReportIaQuizQuestion(question.id, reportReason);
        setReportState("sent");
    }

    return (
        <div className="ia-page">
            <header className="ia-header">
                <div className="ia-header-left">
                    <img src="/paroles-agents-logo.png" alt="SAPPM" className="ia-header-logo"/>
                    <div>
                        <span className="ia-brand-title">Paroles d'Agents</span>
                        <span className="ia-brand-tagline">Le dialogue social à votre service</span>
                    </div>
                </div>
                {onBack && <button className="ia-btn-back" onClick={onBack}>← Retour à la page d'accueil</button>}
            </header>

            <div className="ia-card">
                {phase === "home" && (
                    <div>
                        <span className="ia-eyebrow">Comprendre · pratiquer · sécuriser</span>
                        <h1 className="ia-title">IA et agents : où en êtes-vous, humainement parlant ?</h1>
                        <p className="ia-subtitle">
                            Un parcours adaptatif de 15 questions, de la découverte de l'IA aux enjeux de
                            cyberdéfense — environ 6 minutes. Pas de piège : si vous ne savez pas, dites-le.
                        </p>
                        <button className="ia-btn-start" onClick={start} disabled={loading}>
                            {loading ? "Chargement…" : "Commencer le parcours"}
                        </button>
                    </div>
                )}

                {phase === "quiz" && question && (
                    <div>
                        <div className="ia-level-chip">Parcours adaptatif</div>
                        <div className="ia-prog-label">Question {index} sur {total} — {question.axis}</div>
                        <h1 className="ia-q-text">{question.prompt}</h1>

                        <div className="ia-options">
                            {[...question.options, "Je ne sais pas"].map((option, i) => {
                                const isUnknownOption = i === question.options.length;
                                let cls = "ia-option-btn" + (isUnknownOption ? " ia-option-unknown" : "");
                                if (answered) {
                                    if (i === feedback.correctOption) cls += " correct";
                                    else if (i === selected) cls += feedback.unknown ? " unknown" : " wrong";
                                } else if (i === selected) {
                                    cls += " selected";
                                }
                                return (
                                    <button key={option} className={cls} disabled={answered}
                                            onClick={() => setSelected(i)}>
                                        {!isUnknownOption &&
                                            <span className="ia-option-letter">{String.fromCharCode(65 + i)}.</span>}
                                        {" "}
                                        <span className="ia-option-text">{option}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {!answered && (
                            <div className="ia-actions-row">
                                <button className="ia-hint-btn" onClick={() => setShowHint(!showHint)}>
                                    💡 {showHint ? question.hint : "Afficher un indice"}
                                </button>
                                <button className="ia-btn-primary" disabled={selected === null || loading} onClick={validate}>
                                    Valider ma réponse
                                </button>
                            </div>
                        )}

                        {answered && feedback && (
                            <div className={`ia-feedback ${feedback.unknown ? "unknown" : feedback.correct ? "correct" : "wrong"}`}>
                                <p><strong>{feedbackTitle(feedback)}</strong></p>
                                <p>{feedback.feedbackText}</p>
                                <div className="ia-actions-row">
                                    {reportState === "closed" && (
                                        <button className="ia-report-link" onClick={() => setReportState("open")}>
                                            Signaler cette question
                                        </button>
                                    )}
                                    {reportState === "open" && (
                                        <span>
                                            <select value={reportReason} onChange={e => setReportReason(e.target.value)}>
                                                {REPORT_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                                            </select>
                                            <button onClick={sendReport}>Envoyer</button>
                                        </span>
                                    )}
                                    {reportState === "sent" && <span>Merci, signalement transmis.</span>}
                                    <button className="ia-btn-primary" onClick={proceed} disabled={loading}>
                                        {pendingNext && pendingNext.done ? "Voir mon résultat" : "Question suivante →"}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {phase === "result" && result && (
                    <div>
                        <span className="ia-eyebrow">Votre parcours est terminé</span>
                        <p className="ia-result-score">{result.score} bonnes réponses sur {result.questionCount}</p>
                        <p className="ia-result-unknown">{unknownMessage(result.unknownCount || 0)}</p>
                        <p className="ia-result-level">{result.levelName}</p>
                        <p>{result.levelMessage}</p>
                        <div style={{margin: "20px 0"}}>
                            {result.axisScores.map(a => (
                                <div className="ia-axis-row" key={a.axis}>
                                    <span style={{width: 140}}>
                                        {a.axis}
                                        {a.unknownCount > 0 &&
                                            <span className="ia-axis-unknown-badge"> · {a.unknownCount} ?</span>}
                                    </span>
                                    <span className="ia-axis-bar-track">
                                        <span className="ia-axis-bar-fill" style={{width: `${a.percent}%`}}/>
                                    </span>
                                    <span>{a.percent}%</span>
                                </div>
                            ))}
                        </div>
                        <p style={{fontSize: 12, color: "#4B9278"}}>Résultat personnel, non enregistré nominativement.</p>
                        <button className="ia-btn-start" onClick={start}>Recommencer avec de nouvelles questions</button>
                    </div>
                )}
            </div>
        </div>
    );
}
